import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="font-display text-xl md:text-2xl font-bold text-slate-950 mb-3">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 text-sm md:text-base">{children}</div>
  </div>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <Navbar />
      <header className="pt-36 pb-12 px-4 bg-slate-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto">
          <span className="eyebrow">Legal</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-950 mt-3">Privacy Policy</h1>
          <p className="text-slate-500 mt-4">Last updated: June 2026</p>
        </div>
      </header>

      <main className="px-4 py-14">
        <div className="max-w-3xl mx-auto">
          <p className="text-slate-600 leading-relaxed mb-10 text-sm md:text-base">
            Fair Rideshare LLC ("Fair Rideshare," "we," "us," or "our") respects your privacy. This Privacy Policy
            explains how we collect, use, share, and protect your information when you visit our website, book a
            vehicle, or communicate with us by phone, email, or text message.
          </p>

          <Section title="1. Information We Collect">
            <p>We collect information you provide directly to us, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Contact details: name, phone number, email address, and mailing address.</li>
              <li>Identity and eligibility details: date of birth/age verification (21+), and driver's license information.</li>
              <li>Booking details: selected vehicle, rental dates, and pickup location.</li>
              <li>Payment information: deposits and weekly payments are processed through Cash App; we do not store full payment card numbers.</li>
              <li>Communications: messages you send us and our responses.</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-6 space-y-1">
              <li>To process and manage your booking, deposit, and weekly payments.</li>
              <li>To verify eligibility and prevent fraud.</li>
              <li>To send rental confirmations, payment reminders, and account updates.</li>
              <li>To respond to your questions and provide customer support.</li>
              <li>To send promotional offers and sales messages where you have consented (see SMS &amp; Email below).</li>
            </ul>
          </Section>

          <Section title="3. SMS / Text Messaging Program & Consent">
            <p>
              By providing your mobile phone number to Fair Rideshare LLC, you consent to receive recurring text
              (SMS/MMS) messages from us. These may include transactional messages (such as booking confirmations,
              pickup details, and payment reminders) and, where you have opted in, promotional and sales messages
              about our vehicles and offers.
            </p>
            <p className="font-semibold text-slate-900">
              No mobile information will be shared with third parties or affiliates for marketing or promotional
              purposes. We will not sell, rent, or share your mobile opt-in data and SMS consent with any third
              parties for marketing purposes. Text messaging originator opt-in data and consent will not be shared
              with any third parties.
            </p>
            <p>
              <strong>Message frequency</strong> varies based on your activity with us. <strong>Message and data
              rates may apply.</strong> Carriers are not liable for delayed or undelivered messages.
            </p>
            <p>
              <strong>To opt out</strong> of text messages at any time, reply <strong>STOP</strong> to any message.
              After you reply STOP, you will receive one final confirmation message and will no longer receive
              SMS messages from us. For help, reply <strong>HELP</strong> or contact us at 215-770-0679 or
              info@phillyrideshare.com.
            </p>
            <p>
              You may opt out of marketing emails using the unsubscribe link in any email or by contacting us.
              Opting out of marketing messages will not affect essential service messages related to an active
              rental. Your participation in our messaging program is not a condition of any purchase. See our{' '}
              <a href="/terms" className="text-amber-600 font-semibold hover:underline">Terms of Service</a> for
              additional program details.
            </p>
          </Section>

          <Section title="4. How We Share Information">
            <p>
              We do not sell your personal information. We may share information with service providers who help us
              operate our business (such as payment, email, and messaging providers), and when required by law or
              to protect our rights, customers, or vehicles.
            </p>
            <p>
              <strong>No mobile information will be shared with third parties or affiliates for marketing or
              promotional purposes.</strong> Text messaging originator opt-in data and consent are never shared
              with any third parties.
            </p>
          </Section>

          <Section title="5. Data Retention & Security">
            <p>
              We retain your information for as long as needed to provide our services and meet legal obligations.
              We use reasonable administrative and technical safeguards to protect your information; however, no
              method of transmission or storage is completely secure.
            </p>
          </Section>

          <Section title="6. Your Choices">
            <p>
              You may request access to, correction of, or deletion of your personal information by contacting us.
              You may also opt out of marketing communications as described above.
            </p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>
              Our services are intended for adults 21 years of age and older. We do not knowingly collect
              information from anyone under 18.
            </p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Changes are effective when posted on this page
              with an updated "Last updated" date.
            </p>
          </Section>

          <Section title="9. Contact Us">
            <p>
              Fair Rideshare LLC<br />
              Philadelphia, PA<br />
              Phone: 215-770-0679<br />
              Email: info@phillyrideshare.com
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
